import { Libraries, useJsApiLoader } from '@react-google-maps/api'
import { useRef, useState } from 'react'
import { GOOGLE_API_KEY } from '../constants'
import { SelectedGoogleLocation } from '../redux-toolkits/shared/shared.type'

const libraries: Libraries = ['places']
const NIGERIA_REGION_CODE = 'ng'
const PLACES_LIBRARY_NAME = 'places'
const GOOGLE_MAPS_SCRIPT_ID = 'google-api-script'
const PLACE_FETCH_FIELDS = ['addressComponents', 'formattedAddress', 'location']
const GOOGLE_PLACES_OK_STATUS = 'OK'
const GOOGLE_PLACES_EMPTY_STATUS = 'ZERO_RESULTS'

type GooglePlacesStatus =
  | typeof GOOGLE_PLACES_OK_STATUS
  | typeof GOOGLE_PLACES_EMPTY_STATUS

type PlaceText = {
  text?: string
}

type PlaceAddressComponent = {
  longText?: string
  long_name?: string
  types: string[]
}

type PlaceLocation =
  | {
      lat: () => number
      lng: () => number
    }
  | {
      lat: number
      lng: number
    }

type Place = {
  fetchFields: (payload: { fields: string[] }) => Promise<void>
  formattedAddress?: string
  addressComponents?: PlaceAddressComponent[]
  location?: PlaceLocation
}

type PlacePrediction = {
  placeId: string
  text?: PlaceText
  mainText?: PlaceText
  secondaryText?: PlaceText
  toPlace: () => Place
}

type AutocompleteSuggestion = {
  placePrediction?: PlacePrediction
}

type AutocompleteRequest = {
  input: string
  includedRegionCodes: string[]
  region: string
  sessionToken?: object
}

type PlacesLibrary = {
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (
      request: AutocompleteRequest,
    ) => Promise<{ suggestions: AutocompleteSuggestion[] }>
  }
  AutocompleteSessionToken: new () => object
}

type GoogleMapsWithLibrary = {
  importLibrary: (libraryName: typeof PLACES_LIBRARY_NAME) => Promise<unknown>
}

type PlacePredictionOption = {
  place_id: string
  description: string
  placePrediction: PlacePrediction
}

const getGoogleMaps = (): GoogleMapsWithLibrary | undefined => {
  const maps = (window as Window & { google?: { maps?: unknown } }).google?.maps

  if (
    maps &&
    typeof maps === 'object' &&
    'importLibrary' in maps &&
    typeof maps.importLibrary === 'function'
  ) {
    return maps as GoogleMapsWithLibrary
  }

  return undefined
}

const getPredictionDescription = (prediction: PlacePrediction): string => {
  const description = prediction.text?.text

  if (description) return description

  return [prediction.mainText?.text, prediction.secondaryText?.text]
    .filter(Boolean)
    .join(', ')
}

const getComponentLongName = (
  component: PlaceAddressComponent,
): string | undefined => component.longText ?? component.long_name

const getLocationCoordinate = (
  location: PlaceLocation,
  coordinate: 'lat' | 'lng',
): number => {
  const value = location[coordinate]
  return typeof value === 'function' ? value() : value
}

const buildSelectedGoogleLocation = async (
  prediction: PlacePrediction,
  fallbackAddress: string,
): Promise<SelectedGoogleLocation> => {
  const place = prediction.toPlace()
  await place.fetchFields({ fields: PLACE_FETCH_FIELDS })

  let city = ''
  let state = ''
  let country = ''

  place.addressComponents?.forEach((component) => {
    if (
      component.types.includes('locality') ||
      component.types.includes('administrative_area_level_2')
    ) {
      city = getComponentLongName(component) ?? city
    }

    if (component.types.includes('administrative_area_level_1')) {
      state = getComponentLongName(component) ?? state
    }

    if (component.types.includes('country')) {
      country = getComponentLongName(component) ?? country
    }
  })

  return {
    address: place.formattedAddress ?? fallbackAddress,
    city,
    state,
    country,
    lat: place.location ? getLocationCoordinate(place.location, 'lat') : '',
    lng: place.location ? getLocationCoordinate(place.location, 'lng') : '',
    destinationGooglePlaceID: prediction.placeId,
  }
}

export const useGoogleMaps = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY!,
    libraries: libraries,
    id: GOOGLE_MAPS_SCRIPT_ID,
  })

  const requestIdRef = useRef(0)
  const sessionTokenRef = useRef<object | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<GooglePlacesStatus>(
    GOOGLE_PLACES_EMPTY_STATUS,
  )
  const [data, setData] = useState<PlacePredictionOption[]>([])

  const setAutoCompleteValue = async (input: string) => {
    const trimmedInput = input.trim()
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    if (!trimmedInput) {
      setData([])
      setStatus(GOOGLE_PLACES_EMPTY_STATUS)
      setLoading(false)
      sessionTokenRef.current = undefined
      return
    }

    const googleMaps = getGoogleMaps()

    if (!isLoaded || !googleMaps) {
      setData([])
      setStatus(GOOGLE_PLACES_EMPTY_STATUS)
      return
    }

    setLoading(true)

    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } =
        (await googleMaps.importLibrary(
          PLACES_LIBRARY_NAME,
        )) as unknown as PlacesLibrary

      const sessionToken =
        sessionTokenRef.current ?? new AutocompleteSessionToken()
      sessionTokenRef.current = sessionToken

      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmedInput,
          includedRegionCodes: [NIGERIA_REGION_CODE],
          region: NIGERIA_REGION_CODE,
          sessionToken,
        })

      if (requestId !== requestIdRef.current) return

      const placePredictions = suggestions.flatMap((suggestion) => {
        if (!suggestion.placePrediction) return []

        return [
          {
            place_id: suggestion.placePrediction.placeId,
            description: getPredictionDescription(suggestion.placePrediction),
            placePrediction: suggestion.placePrediction,
          },
        ]
      })

      setData(placePredictions)
      setStatus(
        placePredictions.length > 0
          ? GOOGLE_PLACES_OK_STATUS
          : GOOGLE_PLACES_EMPTY_STATUS,
      )
    } catch {
      if (requestId === requestIdRef.current) {
        setData([])
        setStatus(GOOGLE_PLACES_EMPTY_STATUS)
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }

  const handleSelect = async (
    address: string,
    placeId: string,
    getMoreInfo: boolean = true,
    onSelect?: (loc?: SelectedGoogleLocation) => void,
  ) => {
    try {
      if (getMoreInfo) {
        const selectedPrediction = data.find((place) => {
          return place.place_id === placeId
        })?.placePrediction

        if (!selectedPrediction) return

        const placeData = await buildSelectedGoogleLocation(
          selectedPrediction,
          address,
        )

        if (onSelect) {
          onSelect(placeData)
        }
        sessionTokenRef.current = undefined
      } else {
        if (onSelect) {
          onSelect(undefined)
        }
      }
    } catch {
      if (onSelect) {
        onSelect(undefined)
      }
    }
  }

  return {
    isLoaded,
    loading,
    status,
    data,
    handleSelect,
    setAutoCompleteValue,
  }
}
